/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { Popconfirm, Tag, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import req from '@sportninja/common/api/request'
import type { OfflinePaymentBatch } from '@sportninja/common/services/teamBalance'
import colors from '@sportninja/common/constants/appColors'
import Price from 'src/components/ts-components/Price'
import InlineTextButton from 'src/components/ActionButton/InlineTextButton'
import { dollarsToMinor } from '../utils/balanceMath'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

type OfflineBatchRowProps = {
  batch: OfflinePaymentBatch
  currencyCode: string
  /**
   * Receives the BATCH, not its id: voiding and verifying send the batch's own
   * `line_kind` back (Sep 23), so the caller never has to infer it.
   */
  onVoidOffline?: (batch: OfflinePaymentBatch) => void
  voidingBatchId?: string | null
  /** D39 — opens the verify re-review for a pending batch. */
  onVerifyOffline?: (batch: OfflinePaymentBatch) => void
  verifyingBatchId?: string | null
  /** R3-F4: shown on the detached-history surface, where the player is not implied by a slot. */
  playerName?: string | null
}

/** Status tags keep their size; the caption beside them is what gives way. */
const tagStyle = css`
  flex-shrink: 0;
  margin: 0;
`

/**
 * ONE offline payment batch line (amount · source · date · voided tag · void action) — the
 * single rendering shared by the slot's expanded row and the registration-level detached
 * history (R3-F4), so void affordances and voided styling can never drift apart.
 */
const OfflineBatchRow = ({
  batch,
  currencyCode,
  onVoidOffline,
  voidingBatchId,
  onVerifyOffline,
  verifyingBatchId,
  playerName,
}: OfflineBatchRowProps) => {
  const voided = batch.voided_at !== null
  const [downloading, setDownloading] = useState(false)

  // R4-#3: the dedicated batch receipt PDF (server-authorized; C10 keeps note/recorder out).
  const downloadReceipt = async () => {
    try {
      setDownloading(true)
      const response = await req(
        `/billing/offline-payments/${batch.id}/receipt/pdf`,
        {
          method: 'GET',
          parseJSON: false,
          skipAutoContentType: true,
          headers: { Accept: 'application/pdf' },
        }
      )
      const blob = await response.blob()
      saveAs(blob, `offline-payment-${batch.id}.pdf`)
    } catch (error) {
      message.error(balanceText('common:balanceOfflineReceiptError'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div
      data-testid={`offline-batch-${batch.id}`}
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 8px 12px;
        border: 1px solid ${colors.SECONDARY_100};
        border-radius: 8px;
        background: ${colors.SECONDARY_300};
        opacity: ${voided ? 0.5 : 1};
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            /*
           * Absorbs the slack so the actions column stays put. min-width:0 lets
           * it actually shrink, and the caption below ellipsises rather than
           * pushing — on a narrow drawer the "Voided" tag was rendering
           * straight over the Receipt button.
           */
            flex: 1;
            min-width: 0;
          `}
        >
          {playerName ? <span>{playerName}</span> : null}
          {/* Fixed width so "$10.00" and "$120.00" do not shunt the caption after
            them to a different x on every row. */}
          <span
            css={css`
              min-width: 68px;
              font-variant-numeric: tabular-nums;
            `}
          >
            <Price
              amount={dollarsToMinor(batch.amount)}
              currency={currencyCode}
            />
          </span>
          <span
            css={css`
              color: ${colors.NEUTRAL_70};
              font-size: 12px;
              /* "Cash · Jul 21, 2026" was wrapping mid-date into two cramped
               lines once the row got tighter. It is one atomic caption — and
               it is the element that YIELDS when the row is short, so the tag
               and the actions beside it always keep their space. */
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
            `}
          >
            {balanceLabel(`common:balanceOfflineSource_${batch.source}`)}
            {batch.recorded_at
              ? ` · ${dayjs(batch.recorded_at).format('MMM D, YYYY')}`
              : ''}
          </span>
          {voided ? (
            <Tag color='default' css={tagStyle}>
              {balanceLabel('common:balanceOfflineVoided')}
            </Tag>
          ) : null}
          {/* D39: a pending batch has settled nothing and counts toward nothing —
            say so plainly, or the row reads as collected money. */}
          {batch.verification_status === 'pending' ? (
            <Tooltip title={balanceText('common:balanceVerifyPendingNotice')}>
              <Tag
                color='warning'
                css={tagStyle}
                aria-label={`pending-offline-${batch.id}`}
              >
                {balanceLabel('common:balanceStatusPending')}
              </Tag>
            </Tooltip>
          ) : null}
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 4px;
            /*
           * A FIXED, left-aligned actions column: right-anchoring a variable
           * set of buttons put "Verify" and "Receipt" at a different x on every
           * row, depending on which actions that batch happened to offer, so
           * they share one left edge instead.
           *
           * Sized to the widest REAL pair (Receipt + Void ~ 118px measured) plus
           * a little slack for longer locales — no more, or short rows are left
           * with a band of dead space at the right.
           */
            flex: 0 0 132px;
          `}
        >
          {/* D39: verifying is what settles the money, so it leads the actions.
            can_verify is the STATE half; the endpoint still enforces the
            target-aware authority, and a 403 there is final. */}
          {batch.can_verify && onVerifyOffline ? (
            <InlineTextButton
              tone='primary'
              isLoading={verifyingBatchId === batch.id}
              onClick={() => onVerifyOffline(batch)}
              aria-label={`verify-offline-${batch.id}`}
              text={balanceLabel('common:balanceVerify')}
            />
          ) : null}
          {/* Sep 23 (D8): cash on the caller's OWN budget line is verified by a
            league admin, never by them — shown, disabled, with the reason. */}
          {!batch.can_verify &&
          batch.verify_blocked_reason === 'own_line' &&
          onVerifyOffline ? (
            <Tooltip
              title={balanceText('common:balanceOfficialOwnLineVerifyTip')}
            >
              {/* antd: a disabled button swallows mouse events — the span receives
                the hover (and the focus) so the tooltip actually shows. */}
              <span tabIndex={0} aria-label={`verify-blocked-${batch.id}`}>
                <InlineTextButton
                  tone='primary'
                  disabled
                  text={balanceLabel('common:balanceVerify')}
                />
              </span>
            </Tooltip>
          ) : null}
          {/* A pending batch has no receipt (the server 404s it), so do not offer one. */}
          {batch.verification_status !== 'pending' ? (
            <InlineTextButton
              tone='neutral'
              isLoading={downloading}
              onClick={downloadReceipt}
              aria-label={`receipt-offline-${batch.id}`}
              text={balanceLabel('common:balanceOfflineReceipt')}
            />
          ) : null}
          {!voided && !batch.can_void && batch.void_blocked_reason ? (
            <Tooltip title={balanceText('common:balanceOfflineVoidBlocked')}>
              {/* antd: a disabled button swallows mouse events — the span receives the hover
                so the tooltip actually shows (R6 needs-validation). */}
              <span aria-label={`void-blocked-${batch.id}`}>
                <InlineTextButton
                  tone='danger'
                  disabled
                  text={balanceLabel('common:balanceOfflineVoid')}
                />
              </span>
            </Tooltip>
          ) : null}
          {!voided && batch.can_void && onVoidOffline ? (
            <Popconfirm
              title={balanceText('common:balanceOfflineVoidConfirm')}
              onConfirm={() => onVoidOffline(batch)}
              okText={balanceLabel('common:balanceOfflineVoid')}
            >
              <InlineTextButton
                tone='danger'
                isLoading={voidingBatchId === batch.id}
                aria-label={`void-offline-${batch.id}`}
                text={balanceLabel('common:balanceOfflineVoid')}
              />
            </Popconfirm>
          ) : null}
        </div>
      </div>

      {/*
       * A1 — the reference and the internal note, as a STACKED secondary block.
       * The caption above is a single nowrap line that already ellipsises to
       * protect the actions column; appending a 100-char terminal reference or a
       * 500-char note in-line would either collide with the buttons or truncate
       * the very thing the rep wrote down. Given its own rows they wrap instead.
       */}
      {batch.reference || batch.note ? (
        <div
          data-testid={`offline-batch-meta-${batch.id}`}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 2px;
            font-size: 12px;
            color: ${colors.NEUTRAL_70};
            /* A reference has no spaces to break on. */
            overflow-wrap: anywhere;
          `}
        >
          {batch.reference ? (
            <span aria-label={`reference-offline-${batch.id}`}>
              {balanceLabel('common:balanceOfflineReference')}:{' '}
              {batch.reference}
            </span>
          ) : null}
          {batch.note ? (
            <span aria-label={`note-offline-${batch.id}`}>{batch.note}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default OfflineBatchRow
